import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Shield, Building2, ArrowRight, CheckCircle } from 'lucide-react';
import { useAuthStore } from '../stores/authStore';
import { useVerificationStore } from '../stores/verificationStore';
import api from '../services/api';

const Welcome = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated, setRequest } = useAuthStore();
  const { setRequest: setVerificationRequest } = useVerificationStore();
  const [request, setLocalRequest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchRequest = async () => {
      try {
        // Fetch request details using token
        const response = await api.get(`/requests/invite/${token}`);

        setLocalRequest(response.data);
        setRequest(response.data);

        // Set verification store context for evidence uploads
        setVerificationRequest(
          response.data.id,
          response.data.clientFileId,
          response.data.firmName
        );

        setLoading(false);
      } catch (err) {
        setError('Invalid or expired invite link');
        setLoading(false);
      }
    };

    fetchRequest();
  }, [token, setRequest, setVerificationRequest]);

  const handleStart = () => {
    if (isAuthenticated) {
      navigate('/summary');
    } else {
      navigate('/password-setup');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
        <div className="text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Shield className="w-8 h-8 text-red-600" />
          </div>
          <h1 className="text-xl font-semibold text-gray-900 mb-2">Link Expired</h1>
          <p className="text-gray-600">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
      <div className="w-full max-w-lg bg-white rounded-2xl shadow-xl overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white p-8">
          <div className="flex items-center justify-center mb-4">
            <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center backdrop-blur-sm">
              <Building2 className="w-8 h-8" />
            </div>
          </div>
          <p className="text-center text-blue-100 text-sm">Verification requested by</p>
          <h2 className="text-center text-2xl font-semibold mt-1">{request?.firmName}</h2>
        </div>

        {/* Content */}
        <div className="p-8">
          <h1 className="text-2xl font-bold text-gray-900 mb-4 text-center">
            Verify your identity securely
          </h1>

          {request?.note && (
            <div className="bg-blue-50 rounded-xl p-4 mb-6 border border-blue-100">
              <p className="text-blue-800 text-sm">{request.note}</p>
            </div>
          )}

          <div className="space-y-4 mb-8">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0">
                <CheckCircle className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="font-medium text-gray-900">Quick & Easy</p>
                <p className="text-sm text-gray-600">Takes 3-5 minutes to complete</p>
              </div>
            </div>

            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                <Shield className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="font-medium text-gray-900">Bank-Grade Security</p>
                <p className="text-sm text-gray-600">Your data is encrypted and protected</p>
              </div>
            </div>

            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center flex-shrink-0">
                <CheckCircle className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <p className="font-medium text-gray-900">You're in Control</p>
                <p className="text-sm text-gray-600">See what you share and revoke anytime</p>
              </div>
            </div>
          </div>

          <div className="bg-gray-50 rounded-xl p-4 mb-6 border border-gray-200">
            <div className="flex justify-between items-center mb-2">
              <span className="text-sm text-gray-600">Verification Type</span>
              <span className="font-semibold text-gray-900">{request?.type}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Level</span>
              <span className="font-semibold text-gray-900">{request?.level}</span>
            </div>
          </div>

          <button
            onClick={handleStart}
            className="btn-primary w-full"
          >
            Start Verification
            <ArrowRight className="w-5 h-5 ml-2" />
          </button>

          <p className="text-center text-xs text-gray-500 mt-4">
            By continuing, you agree to our Terms of Service and Privacy Policy
          </p>
        </div>
      </div>
    </div>
  );
};

export default Welcome;
